import { useEffect, useState } from 'react';
import { ensureMdExtension } from '@rbv/shared';
import { index } from '../worker/client.js';
import type { LinkSuggestion } from '../worker/api.js';

export interface CanvasToolbarProps {
  onAddText: () => void;
  onAddNote: (path: string) => void;
  onAddLink: () => void;
  onAddGroup: () => void;
  onZoomFit: () => void;
}

/** Top-left overlay toolbar: add text/note/link/group, zoom-to-fit, hint. */
export function CanvasToolbar(props: CanvasToolbarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinkSuggestion[]>([]);

  useEffect(() => {
    if (!pickerOpen) return;
    let alive = true;
    void index.suggestLinks(query, undefined, 12).then((r) => {
      if (!alive) return;
      const seen = new Set<string>();
      setResults(
        r.filter((s) => {
          if (s.kind === 'heading' || !s.path || seen.has(s.path)) return false;
          seen.add(s.path);
          return true;
        }),
      );
    });
    return () => {
      alive = false;
    };
  }, [pickerOpen, query]);

  const pick = (path: string) => {
    props.onAddNote(path);
    setPickerOpen(false);
    setQuery('');
  };

  return (
    <div className="cv-toolbar" onPointerDown={(e) => e.stopPropagation()}>
      <div className="cv-toolbar-row">
        <button className="cv-tool" title="Add text card" onClick={props.onAddText}>
          ▭
        </button>
        <button
          className={`cv-tool${pickerOpen ? ' active' : ''}`}
          title="Add note card"
          onClick={() => setPickerOpen(!pickerOpen)}
        >
          📄
        </button>
        <button className="cv-tool" title="Add web link card" onClick={props.onAddLink}>
          🔗
        </button>
        <button className="cv-tool" title="Add group" onClick={props.onAddGroup}>
          ▣
        </button>
        <button className="cv-tool" title="Zoom to fit" onClick={props.onZoomFit}>
          ⛶
        </button>
      </div>
      {pickerOpen && (
        <div className="cv-picker">
          <input
            autoFocus
            placeholder="Search notes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = results[0];
                if (first) pick(first.path);
                else if (query.trim()) pick(ensureMdExtension(query.trim()));
              } else if (e.key === 'Escape') {
                setPickerOpen(false);
              }
            }}
          />
          <div className="cv-picker-results">
            {results.map((s) => (
              <button
                key={`${s.id}:${s.linkText}`}
                className="cv-picker-item"
                onClick={() => pick(s.path)}
              >
                <span>{s.display}</span>
                <span className="muted">{s.path}</span>
              </button>
            ))}
            {query.trim() && !results.some((s) => s.path === ensureMdExtension(query.trim())) && (
              <button
                className="cv-picker-item"
                onClick={() => pick(ensureMdExtension(query.trim()))}
              >
                <span>Add by path</span>
                <span className="muted">{ensureMdExtension(query.trim())}</span>
              </button>
            )}
          </div>
        </div>
      )}
      <div className="cv-hint muted">Double-tap canvas to add a card · drag ○ to connect</div>
    </div>
  );
}
